/**
 * Report and action-plan shapes: the student's mastery sheet, the teacher's
 * class and student action plans, the cross-paper reads (repeat strugglers,
 * the term report) and the per-question helplines.
 *
 * Every figure in these is derived on the server from `QuestionResult` and
 * `Attempt` rows — the client renders, it never computes a band or a mark.
 *
 * Also here: the band VOCABULARY (what each band tells a teacher to do) and the
 * three small pure helpers both sides need. They live in the contract rather
 * than in `@cwy/core` because the browser renders the legend and cannot import
 * core, and a second copy of a legend in the web app is the copy that drifts.
 */
import { z } from 'zod';
import type { Board, Grade, BinColor, PaperMode, QuestionType, HomeworkItem } from './domain';

// ─────────────────────────── the band vocabulary ───────────────────────────

/** Best first — the order a student's own sheet reads in. */
export const REPORT_BIN_ORDER: BinColor[] = ['GREEN', 'PURPLE', 'BLUE', 'ORANGE', 'PINK'];

export type BinCounts = Record<BinColor, number>;

/**
 * What a band tells the teacher to DO.
 *
 * The schools' printed report closes on a three-line legend — Orange
 * demonstrate, Blue peer support, Green maintain — and that legend is the only
 * reason the colours are on the page. Five bands need five instructions, and
 * the two new ones are not invented: Purple is "right, but slow", whose fix is
 * a timed drill and not a re-teach, and Pink is the band that never started.
 */
export const BAND_ACTION: Record<BinColor, string> = {
  GREEN: 'Maintain with practice',
  PURPLE: 'Drill for speed — the method is already there',
  BLUE: 'Fix with peer support',
  ORANGE: 'Demonstrate — teach it a different way',
  PINK: 'Re-teach from the basics',
};

/** The same five, with the room the foot of a sheet has. Display strings only. */
export const BAND_ACTION_FULL: Record<BinColor, string> = {
  GREEN: 'Maintain with practice — keep sitting papers.',
  PURPLE: 'Drill for speed — the method is already there.',
  BLUE: 'Fix with peer support, then task cards and tips.',
  ORANGE: 'Peer support first, then theory, explanations and solved examples.',
  PINK: 'Re-teach from the basics — they read the scaffolding first, AI tutor on hand.',
};

/** What is true of every band, said once rather than five times. */
export const STANDING_NOTE =
  'Videos and practice sheets are available on every question, in every band — Green and Purple keep practising.';

/**
 * The order the work happens in, and it is not the same for both people. A
 * teacher spends class time where the most children are stuck, so starts at
 * Pink; a student working alone starts at Blue, one nudge from Green.
 */
export const WORK_ORDER = {
  teacher: ['PINK', 'ORANGE', 'BLUE'] as BinColor[],
  student: ['BLUE', 'ORANGE', 'PINK'] as BinColor[],
  studentNote: 'then revisit Green and Purple to keep them there',
};

/** Orange and Pink are the two bands a teacher has to spend class time on. */
export const NEEDS_RETEACH: BinColor[] = ['ORANGE', 'PINK'];

/** The two bands that need the teacher rather than practice. */
export type ReteachBin = 'ORANGE' | 'PINK';

/**
 * What the question asked the student to DO.
 *
 * The Sub-topic column already says what it was about. The type is the only
 * thing that tells a student why one question was worth five marks and its
 * neighbour one.
 */
export const QUESTION_DESCRIPTOR: Record<QuestionType, string> = {
  MCQ: 'Recall and identify the correct statement',
  SHORT_DESC: 'Define the term and give one example',
  MEDIUM_DESC: 'Explain the idea, with a diagram where it helps',
  LONG_DESC: 'Derive the result and state your assumptions',
  AUDIO: 'Read aloud and explain in your own words',
};

// ─────────────────────────── pure helpers ───────────────────────────

export const emptyBinCounts = (): BinCounts => ({
  GREEN: 0,
  PURPLE: 0,
  BLUE: 0,
  ORANGE: 0,
  PINK: 0,
});

export const binTotal = (counts: BinCounts): number =>
  REPORT_BIN_ORDER.reduce((sum, bin) => sum + counts[bin], 0);

/**
 * The band the middle of a group sits in.
 *
 * Not the average — an average of ordinal bands names no band — and not the
 * mode, which flips on a single vote and can hand a row a Green header while
 * most of the class sits below it. The median moves with the marks, so the
 * colour on a row never argues with the percentage printed beside it.
 */
export function medianBin(counts: BinCounts): BinColor {
  const total = binTotal(counts);
  if (total === 0) return 'PINK';
  let seen = 0;
  for (const bin of REPORT_BIN_ORDER) {
    seen += counts[bin];
    if (seen * 2 >= total) return bin;
  }
  return 'PINK';
}

/**
 * Split `total` whole units in proportion to `weights`, by largest remainder,
 * so the parts always add back up to the whole. Five band shares that total
 * 99% are the kind of thing a teacher spots and then distrusts the sheet over.
 */
export function largestRemainder(weights: number[], total: number): number[] {
  const sum = weights.reduce((s, w) => s + w, 0);
  if (sum <= 0 || total <= 0) return weights.map(() => 0);
  const exact = weights.map((w) => (w / sum) * total);
  const whole = exact.map((e) => Math.floor(e));
  let left = total - whole.reduce((s, w) => s + w, 0);
  const byRemainder = exact.map((e, i) => ({ i, rest: e % 1 })).sort((a, b) => b.rest - a.rest);
  for (const { i } of byRemainder) {
    if (left <= 0) break;
    whole[i] += 1;
    left -= 1;
  }
  return whole;
}

/** One band's share of the questions a child actually reached. */
export function bandShare(counts: BinCounts, bin: BinColor): number {
  const reached = binTotal(counts);
  return reached === 0 ? 0 : counts[bin] / reached;
}

// ─────────────────────────── the mastery sheet ───────────────────────────

/** One question on a student's own sheet. */
export interface MasterySheetRow {
  /** Pending answers have no mark or bucket; 'grading' means the worker is still running. */
  reviewReason?: string | null;
  questionId: string;
  /** Position in the paper — Q1 is the first question, not the first row. */
  qNumber: number;
  questionText: string;
  type: QuestionType;
  /** Maximum marks — the MM column. */
  maxMarks: number;
  /** Marks obtained — the MO column. */
  marksObtained: number;
  secondsTaken: number;
  allocatedSeconds: number;
  overTime: boolean;
  /** A CHAPTER. The UI says Topic; the schema says chapter. See AGENTS.md. */
  topicName: string;
  subtopicId: string;
  subtopicName: string;
  descriptor: string;
  /** Board papers on file that asked this — how much practice is waiting. */
  practisePapers: number;
  /** Videos on hand for this sub-topic: the question's own and the school's picks. */
  videos: number;
  /** Ladder rungs unlocked — every wrong attempt past the first opens one. */
  hints: number;
  /** 1 when the re-teach content opened (third attempt), else 0. */
  theory: number;
  /** 1 when this question left a card in the review queue. */
  taskCards: number;
  /**
   * The band, or null when the question was never reached. A question the
   * child did not get to is not Pink — Pink means three attempts and no solve
   * — and the class report already counts it as QNA rather than a band.
   */
  bin: BinColor | null;
  /**
   * What went wrong, for this child. Deliberately NOT how many others got it
   * wrong — a cohort count on a student's own report is a class comparison.
   */
  commonError: string | null;
}

export interface MasterySheetReport {
  header: {
    studentPaperId: string;
    studentId: string;
    studentName: string;
    /** "Grade 10 · A", or just the grade for a student with no section. */
    gradeSection: string;
    subject: string;
    paperTitle: string;
    board: Board;
    grade: Grade;
    mode: PaperMode;
    submittedAt: string | null;
    totalEarned: number;
    totalPossible: number;
  };
  /** Best band first, paper order within a band; unreached questions last. */
  rows: MasterySheetRow[];
  binCounts: BinCounts;
  footer: {
    /** Percentage of the paper's marks earned. */
    average: number;
    /** Questions that ran past their allocated time. */
    speedUp: number;
    tips: number;
    theory: number;
    /** Questions that fell through to the scaffold path. */
    scaffold: number;
    /** Questions worth taking to the tutor — Blue, Orange and Pink. */
    ai: number;
    notAttempted: number;
    /** Marks still recoverable by clearing Blue, Orange and Pink on attempt one. */
    marksWaiting: number;
    marksWaitingNote: string;
  };
}

/** One entry in the Progress tab's paper picker. */
export interface MasterySheetOption {
  studentPaperId: string;
  paperId: string;
  title: string;
  subject: string;
  mode: PaperMode;
  date: string;
  percentage: number;
  binCounts: BinCounts;
}

// ─────────────────────────── the action plans ───────────────────────────

export interface ActionPlanHeader {
  paperId: string;
  /** A short printable code, as the paper reports carry above the title. */
  paperCode: string;
  paperTitle: string;
  subject: string;
  board: Board;
  grade: Grade;
  mode: PaperMode;
  /** The class this plan reads. Null for a workspace-wide read with no class. */
  classroomId: string | null;
  gradeSection: string;
  className: string;
  principal: string;
  /** Everyone who holds this subject in this class — co-teachers included. */
  subjectTeacher: string;
  date: string;
  totalMarks: number;
}

interface QuestionBase {
  questionId: string;
  qNumber: number;
  type: QuestionType;
  descriptor: string;
  subtopicName: string;
  maxMarks: number;
}

export interface ReteachStudent {
  studentId: string;
  name: string;
  bin: ReteachBin;
}

export interface ClassQuestionRow extends QuestionBase {
  /** The class's average on this question, as a percentage of its marks. */
  averagePercent: number;
  bin: BinColor;
  binCounts: BinCounts;
  /** Children who started the paper and never reached this question. */
  notAttempted: number;
  /** The children in Orange or Pink — who the re-teach is actually for. */
  reteachStudents: ReteachStudent[];
  /** Half the class or more is in Orange/Pink: this one is whole-class time. */
  wholeClass: boolean;
  allocatedSeconds: number;
  /** What the class spent on it, averaged over those who reached it. */
  averageSeconds: number;
  commonError: string;
}

export interface ChapterFooter {
  questionsToReteach: number;
  peerReady: number;
  notAttempted: number;
  marksLost: number;
  marksLostNote: string;
}

export interface ClassTopicSection {
  topicId: string;
  topicName: string;
  averagePercent: number;
  bin: BinColor;
  binCounts: BinCounts;
  rows: ClassQuestionRow[];
  footer: ChapterFooter;
}

/** One child in the overall Pink / Orange split. */
export interface BandStudent {
  studentId: string;
  name: string;
  rollNumber: string;
  /** Question numbers this child left in Pink, in paper order. */
  pink: number[];
  /** Question numbers this child closed in Orange, in paper order. */
  orange: number[];
}

export interface FocusTopic {
  topicName: string;
  averagePercent: number;
  bin: BinColor;
  action: string;
}

export interface ClassActionPlanReport {
  header: ActionPlanHeader;
  /** Submitted / on the roster, as the paper report prints it. */
  submitted: number;
  totalStudents: number;
  classAveragePercent: number;
  binCounts: BinCounts;
  topics: ClassTopicSection[];
  /** The weakest topics, lifted out of the ranking so they are not read last. */
  startHere: FocusTopic[];
  footer: ChapterFooter & { topicsToReteach: number };
  /**
   * The class bifurcated by the two bands that need the teacher. Each child
   * is in ONE group — the worse band they reached on the paper — so the two
   * lists add up to a headcount rather than repeating names.
   */
  bandStudents: Record<ReteachBin, BandStudent[]>;
  /** Children who sat the paper with nothing in Orange or Pink. */
  clearOfReteach: number;
}

export interface StudentQuestionRow extends QuestionBase {
  awaitingReview?: boolean;
  marksObtained: number;
  bin: BinColor | null;
  action: string;
  attempted: boolean;
  /** What the rest of the class did here — the teacher's column, not the child's. */
  classAveragePercent: number;
}

export interface StudentTopicSection {
  topicId: string;
  topicName: string;
  earned: number;
  possible: number;
  averagePercent: number;
  bin: BinColor;
  binCounts: BinCounts;
  rows: StudentQuestionRow[];
}

export interface StudentActionPlanReport {
  header: ActionPlanHeader & {
    studentPaperId: string;
    studentId: string;
    studentName: string;
    rollNumber: string;
    totalEarned: number;
    totalPossible: number;
    percent: number;
    classAveragePercent: number;
    submitted: boolean;
  };
  binCounts: BinCounts;
  topics: StudentTopicSection[];
  focus: FocusTopic[];
  footer: {
    topicsMastered: number;
    topicsToReteach: number;
    notAttempted: number;
    marksWaiting: number;
    marksWaitingNote: string;
  };
}

/** One (paper × class) on the teacher's picker. */
export interface ClassPlanOption {
  paperId: string;
  classroomId: string | null;
  classroomName: string;
  title: string;
  subject: string;
  mode: PaperMode;
  date: string;
  classAveragePercent: number;
  submitted: number;
  totalStudents: number;
  binCounts: BinCounts;
}

/** One row of the student strip, once a paper is chosen. */
export interface StudentPlanOption {
  studentPaperId: string | null;
  studentId: string;
  studentName: string;
  rollNumber: string;
  percent: number;
  /** False while mid-paper; null when they have not started it. */
  submitted: boolean | null;
  binCounts: BinCounts;
}

/** What the caller may read, said out loud on the console. */
export interface ReadingScope {
  /** School admins read every class and every subject in their workspace. */
  readsEverySubject: boolean;
  classrooms: { id: string; name: string; subjectNames: string[] }[];
}

// ─────────────────────────── peer pairing ───────────────────────────

export interface PairedQuestion {
  questionId: string;
  qNumber: number;
  subtopicName: string;
}

export interface PeerPairSuggestion {
  learnerId: string;
  learnerName: string;
  mentorId: string;
  mentorName: string;
  /** What this mentor can actually sit down and explain. */
  questions: PairedQuestion[];
  /** Questions the learner owes that this mentor cannot cover. */
  uncovered: PairedQuestion[];
}

export interface PairingPlan {
  paperId: string;
  pairs: PeerPairSuggestion[];
  /** Too much of the class is Orange here — re-teach it instead of pairing. */
  reteachQuestions: (PairedQuestion & { orangePercent: number })[];
  /** Children who owe work that nobody in this class solved first time. */
  unpaired: { studentId: string; studentName: string; questions: number }[];
}

// ─────────────────────────── across papers ───────────────────────────

export interface StrugglerTopic {
  topicId: string;
  topicName: string;
  /** How many different papers it went wrong in. */
  papers: number;
  /** How many papers put this topic in front of them at all. */
  opportunities: number;
  /** Questions in Orange or Pink across all of them. */
  questions: number;
}

export interface RepeatStruggler {
  studentId: string;
  studentName: string;
  rollNumber: string;
  papersSat: number;
  averagePercent: number;
  /** Worst first: the topics that keep coming back. */
  repeatTopics: StrugglerTopic[];
  reteachQuestions: number;
}

export interface TermReport {
  studentId: string;
  studentName: string;
  rollNumber: string;
  gradeSection: string;
  board: Board;
  grade: Grade;
  /** The first and last paper this report reads — the term, as the child sat it. */
  termStart: string | null;
  termEnd: string | null;
  /** Null when the reader may see every subject; otherwise the ones covered. */
  scopedTo: string[] | null;
  papersSat: number;
  averagePercent: number;
  /** Every question they reached, across every paper — the overall band analysis. */
  binCounts: BinCounts;
  bySubject: {
    subject: string;
    papers: number;
    averagePercent: number;
    firstPercent: number;
    lastPercent: number;
    binCounts: BinCounts;
  }[];
  progress: {
    topicId: string;
    topicName: string;
    subject: string;
    start: BinColor;
    end: BinColor;
    papers: number;
  }[];
  strengths: { topicId: string; topicName: string; subject: string }[];
  growth: { topicId: string; topicName: string; subject: string; questions: number }[];
  pressurePoints: {
    kind: 'recurring' | 'time';
    topicName: string;
    subject: string;
    detail: string;
  }[];
  nextSteps: string[];
  /** From the effort log — time on task across the child's sittings. */
  effort: { totalHours: number; avgSessionMin: number; daysPerWeek: number };
}

// ─────────────────────────── helplines ───────────────────────────

/**
 * Everything a teacher can reach from one question on the class report.
 *
 * TEACHER-ONLY. `correctAnswer` and `markingScheme` are the answer key, which
 * every student payload strips. This bundle is safe for one reason: a class
 * report is a teacher's document, read after the paper closed.
 */
export interface HelplineBundle {
  questionId: string;
  subtopicId: string;
  subtopicName: string;
  topicName: string;
  maxMarks: number;
  /** T — the light hint, then the strong one, in ladder order. */
  tips: string[];
  /** E — theory plus the worked examples. */
  theoryRef: string;
  workedExamples: { step: string; explanation: string }[];
  scaffoldHref: string;
  /** Children in this class who reached it. */
  reached: number;
  /**
   * A — the answer and where its marks are. No per-step "lost by": marks are
   * stored per question, with the attempt multiplier in them, so a step count
   * would be a guess presented as a reading.
   */
  correctAnswer: string;
  markingScheme: { step: string; marks: number }[];
  /** M — what tends to go wrong, from the bank. Uncounted, for the same reason. */
  commonErrors: { description: string }[];
  videos: { title: string; url: string }[];
  practice: { title: string; kind: string; href: string; estMinutes: number }[];
}

export interface ResourcePicks {
  videos: string[];
  practice: string[];
}

// ─────────────────────────── homework ───────────────────────────

/** Half an hour a night off one paper. The preview prints it; the generator cuts at it. */
export const HOMEWORK_CAP_SECONDS = 30 * 60;

/** A generated list on the teacher's preview, before and after Send. */
export interface HomeworkDraft {
  homeworkId: string;
  studentId: string;
  paperId: string;
  paperTitle: string;
  items: (HomeworkItem & { label: string })[];
  estTotalSeconds: number;
  /** Items that did not fit under the cap, counted rather than dropped. */
  heldBack: number;
  flags: string[];
  /** A note in the teacher's voice, drafted from the list. Editable. */
  message: string;
  /**
   * Which draft this is. Send echoes it back, so `keep` is only ever read
   * against the list the teacher was looking at — never one regenerated since.
   */
  draftedAt: string;
  sentAt: string | null;
}

/** An item as the child reads it: what to do, and a link that opens. */
export interface StudentHomeworkItem {
  label: string;
  /** In-app path, or an absolute URL for a video. */
  href: string;
  external: boolean;
  estMinutes: number;
  band: BinColor;
}

export interface StudentHomework {
  homeworkId: string;
  paperId: string;
  paperTitle: string;
  teacherName: string;
  message: string;
  items: StudentHomeworkItem[];
  sentAt: string;
}

/**
 * Send one list. `keep` names the generated items to send, by index — the
 * teacher can drop what the generator built and reword the note, but never add
 * a link of her own to a child's list.
 *
 * The indexes are into the DRAFT named by `draftedAt`, which a send never
 * changes, so sending the same body twice sends the same list twice.
 */
export const homeworkSendBody = z.object({
  keep: z.array(z.number().int().min(0)).max(60),
  message: z.string().max(1200),
  draftedAt: z.string().datetime(),
});
export type HomeworkSendBody = z.infer<typeof homeworkSendBody>;

/**
 * Toggle one pick on a sub-topic. A video is a web link; a practice set is an
 * in-app path, because it rides into a child's homework as a link they trust.
 */
export const resourcePickBody = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('video'), url: z.string().url().max(500).startsWith('http') }),
  z.object({
    kind: z.literal('practice'),
    url: z
      .string()
      .max(200)
      .regex(/^\/[a-z0-9/_-]*$/i, 'A practice pick is an in-app path.'),
  }),
]);
export type ResourcePickBody = z.infer<typeof resourcePickBody>;
